import styled from '@emotion/styled';
import { TinyVariant } from '@shared/constants/components/tinyUtils';
import { IconName } from '../images/IconMap';
import Icons from '../images/Icons';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { BodyXSmall } from '../text/Typography';
import TinyButton from '../tiny/TinyButton';
import { TinyIconButton } from '../tiny/TinyIconsButton';
import TinyTag from '../tiny/TinyTag';
import {
    ActionListCard,
    ActionListRow,
    MixinAdjacentRowDivider,
    MixinRowTruncate,
    RowCheckbox,
} from './CommonActionListStyles';

/** One icon-only action shown at the right end of a row (open, remove, …). */
export interface IItemActionListAction {
    icon: IconName;
    /** Accessible name; also the tooltip when {@link showTooltip} is set. */
    label: string;
    onClick: () => void;
    disabled?: boolean;
    variant?: TinyVariant;
    showTooltip?: boolean;
    showLabel?: boolean;
}

export interface IItemActionListItem {
    /** Stable key, and the value reported by selection callbacks. */
    id: string;
    label: string;
    /** Full value behind a truncated label (a path, a URL) — surfaced as the row's title attribute. */
    title?: string;
    icon?: IconName;
    /** Short kind/status word shown at the right of the label, e.g. "ASSET". */
    identifier?: string;
    /** Colour of the identifier tag. Default reads as muted metadata; pass 'accent' to make one stand out. */
    identifierVariant?: TinyVariant;
    actions?: IItemActionListAction[];
    disabled?: boolean;
    prefixTag?: React.ReactNode;
}

interface IItemActionListProps {
    items: IItemActionListItem[];
    /** Shown in place of the rows when `items` is empty. Omit to render nothing but the add row. */
    emptyText?: string;
    /**
     * Selected ids. Passing this (with {@link onSelectionChange}) turns on the leading checkbox column;
     * omit both for a plain list.
     */
    selectedIds?: string[];
    onSelectionChange?: (selectedIds: string[]) => void;
    /** Label for the trailing add row. Omit (or omit {@link onAdd}) to hide it. */
    addLabel?: string;
    /** Receives the row element so a caller can anchor a menu to it. */
    onAdd?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    addDisabled?: boolean;
    /** Render labels in a monospace face — right for file names, paths, and identifiers. */
    monospaceLabels?: boolean;
}

/**
 * A bordered list of items, each with an optional checkbox, icon, kind tag, and icon-only actions, over
 * an optional "add" row.
 *
 * The add row lives inside the same bordered card rather than as a button beneath it, so the list reads
 * as one editable region: the row it adds appears exactly where the control sits. Its dashed top border
 * separates "what's here" from "add another" without implying it's a seventh item.
 */
export default function ItemActionList({
    items,
    emptyText,
    selectedIds,
    onSelectionChange,
    addLabel,
    onAdd,
    addDisabled,
    monospaceLabels,
}: Readonly<IItemActionListProps>) {
    const selectable = !!selectedIds && !!onSelectionChange;
    const showAddRow = !!addLabel && !!onAdd;

    const toggle = (id: string) => {
        if (!selectedIds || !onSelectionChange) return;
        onSelectionChange(selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id]);
    };

    return (
        <ActionListCard>
            {items.length === 0 && emptyText && <EmptyRow>{emptyText}</EmptyRow>}
            {items.map((item) => (
                <ItemRow key={item.id} title={item.title}>
                    {selectable && (
                        <RowCheckbox
                            size="small"
                            checked={selectedIds.includes(item.id)}
                            disabled={item.disabled}
                            slotProps={{ input: { 'aria-label': item.label } }}
                            onChange={() => toggle(item.id)}
                        />
                    )}
                    {item.icon && (
                        <RowIcon>
                            <Icons name={item.icon} size={16} />
                        </RowIcon>
                    )}
                    <LabelRow>
                        <RowLabel $mono={monospaceLabels}>{item.label}</RowLabel>
                        {item.prefixTag}
                    </LabelRow>

                    {item.identifier && (
                        <TinyTag title={item.identifier} variant={item.identifierVariant ?? 'diminished'} />
                    )}
                    {item.actions?.map((action) =>
                        action.showLabel ? (
                            <TinyButton
                                key={action.label}
                                icon={action.icon}
                                title={action.label}
                                tooltip={action.showTooltip ? action.label : undefined}
                                variant={action.variant}
                                disabled={action.disabled || item.disabled}
                                onClick={action.onClick}
                            />
                        ) : (
                            <TinyIconButton
                                key={action.label}
                                icon={action.icon}
                                label={action.label}
                                tooltip={action.showTooltip ? action.label : undefined}
                                variant={action.variant}
                                disabled={action.disabled || item.disabled}
                                onClick={action.onClick}
                            />
                        )
                    )}
                </ItemRow>
            ))}
            {showAddRow && (
                <AddRow type="button" disabled={addDisabled} onClick={onAdd}>
                    <Icons name="plus" size={16} weight="bold" />
                    {addLabel}
                </AddRow>
            )}
        </ActionListCard>
    );
}

const ItemRow = styled(ActionListRow)`
    ${(props) => MixinAdjacentRowDivider(props.theme)}
`;

// Not the shared `RowIcon`, which nudges itself down for a top-aligned multi-line row: these rows are
// single-line and vertically centred, and want the icon muted like the rest of the row's metadata.
const RowIcon = styled.span`
    display: inline-flex;
    flex: 0 0 auto;
    color: ${(props) => props.theme.color.textBody};
`;

const RowLabel = styled.span<{ $mono?: boolean }>`
    flex: 1;
    min-width: 0;
    font-size: 0.8125rem;
    color: ${(props) => props.theme.color.text};
    font-family: ${(props) => (props.$mono ? `'Courier New', Courier, monospace` : 'inherit')};
    ${MixinRowTruncate}
`;

const EmptyRow = styled(BodyXSmall)`
    padding: 0.75rem 0.875rem;
    color: ${(props) => props.theme.color.textBody};
    font-style: italic;
`;

const AddRow = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: none;
    border-top: 1px dashed ${(props) => props.theme.color.border};
    background: transparent;
    font-family: inherit;
    font-size: 0.8125rem;
    font-weight: 500;
    text-align: left;
    color: ${(props) => props.theme.color.link};
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => props.theme.color.cardHover};
    }

    ${MixinFocusVisible}

    &:disabled {
        cursor: default;
        opacity: 0.45;
    }
`;

const LabelRow = styled.div`
    display: flex;
    flex-grow: 1;
    gap: 0.25rem;
    flex-direction: column;
`;
